<template>
  <v-expansion-panels
    flat
    bg-color="transparent"
    class="howtos"
    v-model="panel"
  >
    <v-expansion-panel>
      <v-expansion-panel-title
        class="font-weight-bold text-uppercase pl-0 d-flex align-center"
        :class="titleClass"
      >
        <div class="d-flex align-center">
          {{ $t('howto.title') }}
          <span
            class="text-caption text-uppercase ml-3 mr-3 text-red font-weight-bold"
          >
            {{ $t('howto.clickHere') }}
          </span>
        </div>
        <template #actions="{ expanded }">
          <ArrowDown
            :color="arrowColor"
            :class="['arrow-icon', { 'rotate-180': expanded }]"
          />
        </template>
      </v-expansion-panel-title>

      <v-expansion-panel-text
        class="howto-content-container py-4"
        :style="{ background: editorBackgroundColor }"
      >
        <GlobalQuillEditor
          :page="pageId"
          :firestoreCollection="'howtos'"
          :allowed-roles="['superAdmin']"
          :readonly="!isSuperAdmin"
          @update:hasContent="hasContent = $event"
        />

        <div
          v-if="!hasContent && !isSuperAdmin"
          class="text-subtitle-1 text-grey-darken-1 mt-4"
        >
          {{ $t('howto.noInstructions') }}
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup>
  import { ref, computed, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import GlobalQuillEditor from '@/components/utilities/GlobalQuillEditor.vue'
  import ArrowDown from '@/assets/icons/ArrowDown.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { useStore } from 'vuex'

  const store = useStore()

  const isSuperAdmin = computed(() => {
    const roles = store.getters['UserData/getRoles'] || []
    return roles.includes('superAdmin')
  })

  const props = defineProps({ page: String })

  const hasContent = ref(false)

  const panel = ref([])
  const route = useRoute()

  const pageId = computed(() => {
    if (props.page) return props.page

    // Extract page name from route path, removing locale prefix if present
    const pathParts = route.path.split('/').filter((part) => part)
    // Remove locale if present (first part if it's 'en', 'pt', etc.)
    if (pathParts.length > 0 && ['en', 'pt'].includes(pathParts[0])) {
      pathParts.shift() // Remove locale
    }
    // Get the last part as the page name (e.g., 'notifications' from 'admin/howtos/notifications')
    return pathParts[pathParts.length - 1] || 'notifications'
  })

  // Watch for route changes and close the panel when locale changes
  watch(
    () => route.path,
    (newPath, oldPath) => {
      if (newPath !== oldPath) {
        // Close the panel when route changes (including locale switches)
        panel.value = []
      }
    }
  )

  const arrowColor = computed(() =>
    currentTheme.value === 'dark' ? '#cecece' : '#383935'
  )
  const titleClass = computed(() =>
    currentTheme.value === 'dark' ? 'text-white' : ''
  )
  const editorBackgroundColor = computed(() =>
    currentTheme.value === 'dark' ? '#272727' : '#ffffff'
  )
</script>

<style>
  .howtos {
    margin-bottom: 50px;
  }
  .howtos .v-expansion-panel-title {
    color: #383935;
    border-bottom: 1px solid #cecece;
  }
  .howtos .v-expansion-panel-header__icon {
    margin-left: 10px;
  }
  .howto-content-container {
    overflow: hidden;
    padding: 24px;
  }
  .howto-content-container img {
    max-width: 100%;
  }
  .howto-content-container .ql-editor {
    min-height: 180px;
    max-height: 400px;
    overflow-y: auto;
  }
  .status-indicator {
    margin-left: 7px;
  }
</style>
